/**
 * League API Service
 * Handles all league-related API calls using AWS Lambda functions
 */

import { fetchAuthSession } from 'aws-amplify/auth';

// Get the API endpoint from amplify outputs
let apiEndpoint = null;

// Initialize API endpoint
const getApiEndpoint = async () => {
  if (apiEndpoint) return apiEndpoint;
  
  try {
    const amplifyConfig = await import('../amplify_outputs.json');
    // Extract API endpoint if available
    // For now, we'll use the Lambda function directly
    apiEndpoint = amplifyConfig.aws_appsync_graphqlEndpoint || '';
    return apiEndpoint;
  } catch (error) {
    console.error('Error loading API config:', error);
    return '';
  }
};

// Helper function to make authenticated API calls
const makeApiCall = async (endpoint, method = 'GET', body = null) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };

    const options = {
      method,
      headers,
      ...(body && { body: JSON.stringify(body) }),
    };

    const response = await fetch(endpoint, options);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API call failed:', error);
    throw error;
  }
};

class LeagueApiService {
  /**
   * Create a new league
   * @param {Object} leagueData - League creation data
   * @returns {Promise<Object>} Created league data
   */
  async createLeague(leagueData) {
    try {
      // For now, we'll use the GraphQL API through the existing service
      // But this is where you'd call the Lambda function directly if needed
      const apiService = (await import('./apiGraphQL.js')).default;
      return await apiService.createLeague(leagueData);
    } catch (error) {
      console.error('Error creating league:', error);
      throw new Error(error.message || 'Failed to create league');
    }
  }

  /**
   * Get all leagues for a user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User's leagues
   */
  async getLeagues(userId) {
    try {
      const apiService = (await import('./apiGraphQL.js')).default;
      return await apiService.getLeagues(userId);
    } catch (error) {
      console.error('Error fetching leagues:', error);
      throw new Error(error.message || 'Failed to fetch leagues');
    }
  }

  /**
   * Get a specific league
   * @param {string} leagueId - League ID
   * @returns {Promise<Object>} League data
   */
  async getLeague(leagueId) {
    try {
      const apiService = (await import('./apiGraphQL.js')).default;
      return await apiService.getLeague(leagueId);
    } catch (error) {
      console.error('Error fetching league:', error);
      throw new Error(error.message || 'Failed to fetch league');
    }
  }

  /**
   * Update league settings
   * @param {string} leagueId - League ID
   * @param {Object} updates - League updates
   * @returns {Promise<Object>} Updated league data
   */
  async updateLeague(leagueId, updates) {
    try {
      const apiService = (await import('./apiGraphQL.js')).default;
      return await apiService.updateLeague(leagueId, updates);
    } catch (error) {
      console.error('Error updating league:', error);
      throw new Error(error.message || 'Failed to update league');
    }
  }

  /**
   * Delete a league
   * @param {string} leagueId - League ID
   * @returns {Promise<Object>} Deleted league data
   */
  async deleteLeague(leagueId) {
    try {
      const apiService = (await import('./apiGraphQL.js')).default;
      return await apiService.deleteLeague(leagueId);
    } catch (error) {
      console.error('Error deleting league:', error);
      throw new Error(error.message || 'Failed to delete league');
    }
  }

  /**
   * Join a league
   * @param {string} leagueId - League ID
   * @param {Object} teamData - Team data for joining
   * @returns {Promise<Object>} Updated league data
   */
  async joinLeague(leagueId, teamData) {
    try {
      // Get current league
      const { league } = await this.getLeague(leagueId);
      
      if (!league) {
        throw new Error('League not found');
      }

      // Add new team to league
      const teams = JSON.parse(league.teams || '[]');
      
      // Check if league is full
      const settings = JSON.parse(league.settings || '{}');
      if (teams.length >= settings.maxTeams) {
        throw new Error('League is full');
      }

      // Check if user already in league
      if (teams.some(team => team.owner === teamData.owner)) {
        throw new Error('You are already in this league');
      }

      const newTeam = {
        id: `team-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        ...teamData,
        points: 0,
        joinedAt: new Date().toISOString(),
      };

      teams.push(newTeam);

      // Update league
      return await this.updateLeague(leagueId, {
        teams: JSON.stringify(teams),
      });
    } catch (error) {
      console.error('Error joining league:', error);
      throw new Error(error.message || 'Failed to join league');
    }
  }

  /**
   * Leave a league
   * @param {string} leagueId - League ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Updated league data
   */
  async leaveLeague(leagueId, userId) {
    try {
      // Get current league
      const { league } = await this.getLeague(leagueId);
      
      if (!league) {
        throw new Error('League not found');
      }

      // Don't allow commissioner to leave
      if (league.creatorId === userId) {
        throw new Error('League commissioner cannot leave. Delete the league instead.');
      }

      // Remove team from league
      const teams = JSON.parse(league.teams || '[]');
      const updatedTeams = teams.filter(team => team.owner !== userId);

      if (updatedTeams.length === teams.length) {
        throw new Error('You are not in this league');
      }

      // Update league
      return await this.updateLeague(leagueId, {
        teams: JSON.stringify(updatedTeams),
      });
    } catch (error) {
      console.error('Error leaving league:', error);
      throw new Error(error.message || 'Failed to leave league');
    }
  }

  /**
   * Send invitation email to join league
   * @param {Object} inviteData - Invitation data
   * @returns {Promise<Object>} Invitation result with token
   */
  async sendInvitation(inviteData) {
    try {
      // For now, generate invitation token locally
      // In production, this would call the Lambda function
      
      const inviteToken = btoa(JSON.stringify({
        leagueId: inviteData.leagueId,
        email: inviteData.invitedEmail,
        teamName: inviteData.teamName,
        timestamp: Date.now()
      }));

      const baseUrl = window.location.origin;
      const inviteLink = `${baseUrl}/join-invite?token=${inviteToken}`;

      console.log('Generated invitation link:', inviteLink);

      // TODO: In production, call Lambda function to send email
      // const response = await fetch(lambdaEndpoint, {
      //   method: 'POST',
      //   body: JSON.stringify(inviteData)
      // });

      return {
        success: true,
        inviteLink,
        inviteToken,
        message: 'Invitation created (email sending requires AWS SES configuration)'
      };
    } catch (error) {
      console.error('Error sending invitation:', error);
      throw new Error(error.message || 'Failed to send invitation');
    }
  }
}

// Export singleton instance
const leagueApiService = new LeagueApiService();
export default leagueApiService;

